import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';

/**
 * The three ANONYMOUS invitation endpoints, which must reach the server even when the tab holds an
 * absolutely-expired session (cc-14, AC-CC14.7 — see the exemption comment in the interceptor).
 *
 * Matched on the request PATH rather than on a whole absolute URL, because `apiBaseUrl` differs by
 * serve configuration: it is `''` under `start:shell` (same-origin through the proxy) and an absolute
 * origin under `local` and `development`. Same style as `error.interceptor.ts`, which identifies the
 * refresh, sign-out and session-cookie endpoints by path fragment.
 */
const ANONYMOUS_INVITATION_PATHS = [
  '/api/v1/auth/authorize/lockout-handoff',
  '/api/v1/auth/authorize/lockout-handoff/unlock',
  '/api/v1/auth/authorize/lockout-handoff/cancel',
  '/api/v1/invitations/validate',
  '/api/v1/invitations/activation-continuation',
  '/api/v1/invitations/activation-continuation/redeem',
];

function isAnonymousInvitationRequest(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return ANONYMOUS_INVITATION_PATHS.some((p) => path.endsWith(p));
}

/**
 * Attaches the session Bearer token to all outbound API requests.
 * Also enforces the 8-hour absolute session timeout (AC-1.4.2).
 * Registered as the FIRST interceptor in the chain (before errorInterceptor).
 * No token → request passes through unchanged (let server enforce auth).
 *
 * EXEMPTION — the three anonymous invitation endpoints (cc-14 code-review fix, Codex `gpt-5.6-sol`
 * 2026-09-07 Major 1; PO decision Clint Good, same day: fix it in cc-14 rather than defer).
 * The absolute-expiry branch below does not merely refuse the request: its side effect,
 * `handleSessionExpiry('absolute')` (`auth.service.ts:205-210`), SCHEDULES A NAVIGATION to
 * `/session-expired`, and nothing downstream can undo it. So an admin who has been signed in for more
 * than eight hours in that tab and then clicks an invitation link is taken to the session-expired
 * screen instead of the activation restart card AC-CC14.7 requires, and the activation request is
 * never sent. cc-14 widened that window: the Microsoft sign-in click now makes a server call where it
 * previously only wrote an in-memory field.
 *
 * The three exempted requests are `[AllowAnonymous]` server-side and are on
 * `TenantResolutionMiddleware`'s exemption list, so they need no token — and an expired one is worse
 * than none, because it is the exact shape that 401'd cc-7 on its live E2E. The stale session is
 * cleared here (its tokens are dead anyway; the redemption either establishes a fresh session or
 * routes to `/activate/error`) but NO navigation is scheduled.
 *
 * Every OTHER request keeps the exact short-circuit semantics it had before, unchanged.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  // AuthService is injected to call handleSessionExpiry(), which clears _currentUser
  // so authService.isAuthenticated() correctly reflects false after absolute timeout.
  const authService = inject(AuthService);

  const isAnonymousInvitation = isAnonymousInvitationRequest(req.url);

  // Enforce absolute 8-hour timeout before attaching the token (AC-1.4.2).
  // Belt-and-suspenders alongside OpenIddict's SetRefreshTokenLifetime(8hr).
  if (sessionService.isAbsoluteExpired()) {
    if (isAnonymousInvitation) {
      sessionService.clearSession();
      return next(req);
    }
    authService.handleSessionExpiry('absolute');
    return throwError(() => new Error('Session absolute timeout'));
  }

  const token = sessionService.getAccessToken();
  const admissionApplication = (req.body as { applicationId?: string } | null)?.applicationId;
  const isCookieAdmission = req.withCredentials && req.url.split('?')[0].endsWith('/session/admit')
    && admissionApplication?.toLowerCase() === 'happen-payroll';
  if (token && !isAnonymousInvitation && !isCookieAdmission) {
    const authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
    return next(authReq);
  }

  return next(req);
};
